// src/features/workflow/pages/WorkflowInboxPage.tsx
// Inbox page for approvers: lists pending WorkflowInstances and lets them
// approve or reject each one synchronously.

import { useState } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useV1WorkflowInstancesList } from "@/api/generated/v1/v1";
import { axiosInstance } from "@/api/client";
import { PageBanner } from "@/shared/components/PageBanner";

// Human-readable label per aggregate_type
function aggregateLabel(type: string | null | undefined): string {
  switch (type) {
    case "quality_check_revision":
      return "برنامهٔ کنترل کیفیت";
    case "bom_revision":
      return "BOM Revision";
    case "routing_revision":
      return "Routing Revision";
    default:
      return type ?? "—";
  }
}

// Link to the aggregate detail page (best-effort)
function aggregateLink(
  type: string | null | undefined,
  id: string | null | undefined
): string | null {
  if (!id) return null;
  switch (type) {
    case "quality_check_revision":
      return `/quality/revisions/${id}`;
    default:
      return null;
  }
}

function InboxItemRow({ inst, isProcessing, onApprove, onReject }: any) {
  const label = aggregateLabel(inst.aggregate_type);
  const link = aggregateLink(inst.aggregate_type, inst.aggregate_id);

  // Fetch the name if it's a quality check revision
  const { data: revision } = useQuery({
    queryKey: ["qualityCheckRevision", inst.aggregate_id],
    queryFn: () => axiosInstance.get(`/api/v1/quality-check-revisions/${inst.aggregate_id}/`).then(res => res.data),
    enabled: inst.aggregate_type === "quality_check_revision",
  });

  const aggregateName = revision ? `${revision.name} — Rev ${revision.revision_no}` : inst.aggregate_id;

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 10,
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.1)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, wordBreak: "break-all" }}>
          {link ? (
            <a
              href={link}
              style={{ color: "var(--color-accent, #60a5fa)", textDecoration: "none" }}
              onClick={(e) => {
                e.preventDefault();
                window.location.href = link;
              }}
            >
              {aggregateName}
            </a>
          ) : (
            aggregateName
          )}
        </div>
        <div style={{ fontSize: 11, opacity: 0.45, marginTop: 4 }}>
          ثبت شده: {inst.started_at ? new Date(inst.started_at).toLocaleString("fa-IR") : "نامشخص"}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button
          onClick={() => onApprove(inst.id)}
          disabled={isProcessing}
          style={{
            background: "rgba(34,197,94,0.2)",
            color: "#4ade80",
            border: "1px solid rgba(34,197,94,0.4)",
            borderRadius: 6,
            padding: "6px 16px",
            cursor: isProcessing ? "not-allowed" : "pointer",
            opacity: isProcessing ? 0.5 : 1,
          }}
        >
          {isProcessing ? "در حال پردازش..." : "تأیید"}
        </button>
        <button
          onClick={() => onReject(inst.id)}
          disabled={isProcessing}
          style={{
            background: "rgba(239,68,68,0.15)",
            color: "#f87171",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 6,
            padding: "6px 16px",
            cursor: isProcessing ? "not-allowed" : "pointer",
            opacity: isProcessing ? 0.5 : 1,
          }}
        >
          رد
        </button>
      </div>
    </div>
  );
}

export function WorkflowInboxPage() {
  const queryClient = useQueryClient();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const { data, isLoading, refetch } = useV1WorkflowInstancesList({
    current_state: "pending_approval",
  } as any);

  const pending = data?.results ?? [];

  async function handleApprove(instanceId: string) {
    setError(null);
    setSuccessMsg(null);
    setProcessingId(instanceId);
    try {
      await axiosInstance.post(
        `/api/v1/workflow/instances/${instanceId}/approve/`
      );
      setSuccessMsg("تأیید با موفقیت انجام شد.");
      refetch();
      // Invalidate quality revisions cache so QualityCheckRevisionDetailPage
      // reflects the new status without a manual refresh.
      queryClient.invalidateQueries({ queryKey: ["v1QualityCheckRevisions"] });
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ??
          err?.response?.data?.non_field_errors?.[0] ??
          "خطا در تأیید. دوباره تلاش کنید."
      );
    } finally {
      setProcessingId(null);
    }
  }

  async function handleReject(instanceId: string) {
    setError(null);
    setSuccessMsg(null);
    setProcessingId(instanceId);
    try {
      await axiosInstance.post(
        `/api/v1/workflow/instances/${instanceId}/reject/`
      );
      setSuccessMsg("رد با موفقیت انجام شد.");
      refetch();
      queryClient.invalidateQueries({ queryKey: ["v1QualityCheckRevisions"] });
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ??
          err?.response?.data?.non_field_errors?.[0] ??
          "خطا در رد. دوباره تلاش کنید."
      );
    } finally {
      setProcessingId(null);
    }
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="صندوق تأیید (Approval Inbox)"
        subtitle="مشاهده و تأیید مواردی که در انتظار بررسی شما هستند"
        icon="📥"
        helpLink="/help/workflow/inbox"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => window.location.href = "/workflow"}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد گردش‌کار
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem", maxWidth: "800px", margin: "0 auto", width: "100%" }}>

      {successMsg && (
        <div
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            background: "rgba(34,197,94,0.15)",
            color: "#4ade80",
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {successMsg}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            background: "rgba(239,68,68,0.15)",
            color: "#f87171",
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {isLoading ? (
        <p style={{ color: "var(--color-text-muted)", textAlign: "center", padding: "2rem" }}>در حال بارگذاری...</p>
      ) : pending.length === 0 ? (
        <p style={{ opacity: 0.6, fontSize: 14, textAlign: "center", padding: "2rem" }}>
          هیچ موردی در انتظار تأیید نیست.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {pending.map((inst: any) => (
            <InboxItemRow
              key={inst.id}
              inst={inst}
              isProcessing={processingId === inst.id}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
